import { useSyncExternalStore } from 'react';
import { QuestionnaireTemplate, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';
import { loadAllPages } from './loadAllPages';

export interface QuestionnaireListParams {
  page: number;
  pageSize: number;
  q?: string;
}

const buildQuery = (params: QuestionnaireListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined
});

export const useQuestionnaires = (
  params: QuestionnaireListParams
): PaginatedResponse<QuestionnaireTemplate> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('questionnaires', cb),
    () => 0,
    () => 0
  );
  return ctx.read('questionnaires', { kind: 'page', ...params }, (p: QuestionnaireListParams) =>
    api.listStaffQuestionnaires(buildQuery(p))
  );
};

export const useQuestionnairesAll = (): QuestionnaireTemplate[] => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('questionnaires', cb),
    () => 0,
    () => 0
  );
  return ctx.read('questionnaires', { kind: 'all' }, () =>
    loadAllPages<QuestionnaireTemplate>((p) => api.listStaffQuestionnaires(p))
  );
};

export interface QuestionnaireMutations {
  create: (questionnaire: QuestionnaireTemplate) => Promise<void>;
  update: (questionnaire: QuestionnaireTemplate) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useQuestionnaireMutations = (): QuestionnaireMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('questionnaires');
  return {
    create: async (questionnaire) => {
      await api.createQuestionnaire(questionnaire);
      after();
    },
    update: async (questionnaire) => {
      await api.updateQuestionnaire(questionnaire);
      after();
    },
    remove: async (id) => {
      await api.deleteQuestionnaire(id);
      after();
    }
  };
};
